import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { AppLayout } from '../../components/layout';
import { BORROWER_NAV } from '../../config/navigation';
import { supabase } from '../../lib/supabase';
import { CATEGORIES } from '../../utils/readinessScoring';
import { Surface, Badge, PageHeader, Grid, Button, EmptyState, Spinner } from '../../ui/components';
import { ClipboardCheck, FileText, Search, Landmark, CheckCircle, Home, Check, Play, Clock, AlertTriangle, Eye, Plus, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import FloatingOwenAssistant from '../../components/common/FloatingOwenAssistant';
import FloatingKnowledge from '../../components/common/FloatingKnowledge';
import { LK_FEATURES } from '../../config/features';
import { useEarlyAccess } from '../../components/common/EarlyAccessProvider';

// ── Status config ───────────────────────────────────────────────
const statusColors = {
  draft: 'neutral',
  in_progress: 'info',
  submitted_for_underwriting: 'warning',
  underwriting_in_progress: 'warning',
  conditionally_cleared: 'info',
  cleared_for_marketplace: 'primary',
  in_marketplace: 'primary',
  bid_accepted: 'success',
  closed: 'success',
  needs_work: 'danger',
};

const statusLabels = {
  draft: 'Draft',
  in_progress: 'In Progress',
  submitted_for_underwriting: 'Under Review',
  underwriting_in_progress: 'Under Review',
  conditionally_cleared: 'Conditionally Cleared',
  cleared_for_marketplace: 'Ready for Lenders',
  in_marketplace: 'In Marketplace',
  bid_accepted: 'Offer Accepted',
  closed: 'Funded',
  needs_work: 'Needs Attention',
};

// ── Journey steps ───────────────────────────────────────────────
// MVP journey ends at "Pre-Underwritten" — the last step the borrower can complete
// inside LoanKnow. Marketplace / offer / funded steps belong to Phase 2 (lender flow)
// and are only shown when LK_FEATURES.lenderMarketplace is enabled.
const MVP_JOURNEY_STEPS = [
  { id: 'draft',                        label: 'Assessment',         Icon: ClipboardCheck },
  { id: 'in_progress',                  label: 'Application',        Icon: FileText },
  { id: 'submitted_for_underwriting',   label: 'Owen Review',        Icon: Search },
  { id: 'cleared_for_marketplace',      label: 'Pre-Underwritten',   Icon: CheckCircle },
];

const PHASE2_JOURNEY_STEPS = [
  ...MVP_JOURNEY_STEPS,
  { id: 'bid_accepted',                 label: 'Offer',              Icon: CheckCircle },
  { id: 'closed',                       label: 'Funded',             Icon: Home },
];

const JOURNEY_STEPS = LK_FEATURES.lenderMarketplace ? PHASE2_JOURNEY_STEPS : MVP_JOURNEY_STEPS;

const getJourneyIndex = (status) => {
  if (status === 'underwriting_in_progress') return 2;
  if (status === 'conditionally_cleared') return 2;
  if (status === 'in_marketplace') return 3;
  if (status === 'needs_work') return 2;
  const idx = JOURNEY_STEPS.findIndex(s => s.id === status);
  return idx >= 0 ? idx : 0;
};

// ── Score tiers ─────────────────────────────────────────────────
const TIERS = [
  { min: 0,  label: 'Getting Started', color: 'var(--lk-gray-400)' },
  { min: 15, label: 'On Your Way',     color: 'var(--lk-warning-500)' },
  { min: 30, label: 'Building Momentum', color: 'var(--lk-warning-400)' },
  { min: 50, label: 'Strong Profile',  color: 'var(--lk-primary-500)' },
  { min: 70, label: 'Excellent',       color: 'var(--lk-success-500)' },
  { min: 90, label: 'Outstanding',     color: 'var(--lk-success-600)' },
];

const getTier = (score) => {
  for (let i = TIERS.length - 1; i >= 0; i--) {
    if (score >= TIERS[i].min) return TIERS[i];
  }
  return TIERS[0];
};

// ── SVG Score Ring ──────────────────────────────────────────────
const ScoreRing = ({ score, size = 160, strokeWidth = 10 }) => {
  const tier = getTier(score);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--lk-gray-100)" strokeWidth={strokeWidth} />
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke={tier.color} strokeWidth={strokeWidth}
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1s ease, stroke 0.4s ease' }}
        />
      </svg>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{ fontSize: '36px', fontWeight: 700, color: tier.color, lineHeight: 1, letterSpacing: '-0.03em' }}>{score}</span>
        <span style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)', marginTop: '2px' }}>of 100</span>
      </div>
    </div>
  );
};

// ── Category mini-bar (for dashboard breakdown) ─────────────────
const CategoryMiniBar = ({ cat, score, maxPoints }) => {
  const pct = maxPoints > 0 ? (score / maxPoints) * 100 : 0;
  const barColor = pct >= 80 ? 'var(--lk-success-500)' : pct >= 50 ? 'var(--lk-primary-500)' : pct >= 25 ? 'var(--lk-warning-500)' : 'var(--lk-gray-200)';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)', fontSize: 'var(--lk-text-sm)' }}>
      <div style={{
        width: 28, height: 28, borderRadius: 'var(--lk-radius-sm)',
        backgroundColor: `${cat.color}14`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {cat.Icon ? <cat.Icon size={14} color={cat.color} /> : null}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--lk-text-xs)', marginBottom: 3 }}>
          <span style={{ color: 'var(--lk-text-secondary)', fontWeight: 500 }}>{cat.label}</span>
          <span style={{ color: barColor, fontWeight: 600 }}>{score}/{maxPoints}</span>
        </div>
        <div style={{ height: 4, borderRadius: 2, backgroundColor: 'var(--lk-gray-100)' }}>
          <div style={{ height: '100%', width: `${pct}%`, backgroundColor: barColor, borderRadius: 2, transition: 'width 0.6s ease' }} />
        </div>
      </div>
    </div>
  );
};

// ── Journey Timeline (horizontal) ──────────────────────────────
const JourneyTimeline = ({ currentIndex }) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    {JOURNEY_STEPS.map((step, i) => {
      const isComplete = i < currentIndex;
      const isCurrent = i === currentIndex;
      const StepIcon = step.Icon;

      return (
        <div key={step.id} style={{ display: 'flex', alignItems: 'center', flex: i < JOURNEY_STEPS.length - 1 ? 1 : 'none' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 56 }}>
            <div style={{
              width: 34, height: 34, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: isComplete ? 'var(--lk-success-500)' : isCurrent ? 'var(--lk-primary-500)' : 'var(--lk-gray-100)',
              color: isComplete || isCurrent ? 'white' : 'var(--lk-gray-400)',
              transition: 'all var(--lk-duration-slow) var(--lk-ease)',
              boxShadow: isCurrent ? '0 0 0 4px var(--lk-primary-100)' : 'none',
            }}>
              {isComplete ? <Check size={16} /> : <StepIcon size={16} />}
            </div>
            <span style={{
              fontSize: 'var(--lk-text-xs)', fontWeight: isCurrent ? 600 : 400, marginTop: 6,
              color: isComplete ? 'var(--lk-success-600)' : isCurrent ? 'var(--lk-primary-600)' : 'var(--lk-text-quaternary)',
              whiteSpace: 'nowrap',
            }}>
              {step.label}
            </span>
          </div>
          {i < JOURNEY_STEPS.length - 1 && (
            <div style={{
              flex: 1, height: 2, marginBottom: 22,
              backgroundColor: isComplete ? 'var(--lk-success-400)' : 'var(--lk-gray-100)',
              transition: 'background-color var(--lk-duration-slow) var(--lk-ease)',
            }} />
          )}
        </div>
      );
    })}
  </div>
);

// ── Next Action Card ────────────────────────────────────────────
const NextActionCard = ({ app, onContinue, onView }) => {
  const status = app?.status;
  if (!status) return null;

  const actions = {
    draft: {
      Icon: Play, color: 'var(--lk-primary-500)', bg: 'var(--lk-primary-50)',
      title: 'Continue Your Assessment',
      desc: 'Pick up where you left off — answer a few questions to get matched with the right loan program.',
      cta: 'Continue Application', variant: 'primary', onClick: () => onContinue(app.id),
    },
    in_progress: {
      Icon: FileText, color: 'var(--lk-primary-500)', bg: 'var(--lk-primary-50)',
      title: 'Complete Your Application',
      desc: 'Finish filling out your application details to submit for underwriting review.',
      cta: 'Continue Application', variant: 'primary', onClick: () => onContinue(app.id),
    },
    submitted_for_underwriting: {
      Icon: Clock, color: 'var(--lk-warning-600)', bg: 'var(--lk-warning-50)',
      title: 'Under Review',
      desc: 'Your application is being reviewed. We\'ll notify you if anything is needed. Average review time: 24-48 hours.',
      cta: 'View Details', variant: 'secondary', onClick: () => onView(app.id),
    },
    underwriting_in_progress: {
      Icon: Search, color: 'var(--lk-warning-600)', bg: 'var(--lk-warning-50)',
      title: 'Underwriting In Progress',
      desc: 'An underwriter is actively reviewing your file. You may receive requests for additional documentation.',
      cta: 'View Details', variant: 'secondary', onClick: () => onView(app.id),
    },
    conditionally_cleared: {
      Icon: ClipboardCheck, color: 'var(--lk-primary-500)', bg: 'var(--lk-primary-50)',
      title: 'Conditions to Clear',
      desc: 'Owen flagged a few conditions. Clear them to finalize your pre-underwritten file.',
      cta: 'View Conditions', variant: 'primary', onClick: () => onView(app.id),
    },
    cleared_for_marketplace: {
      Icon: CheckCircle, color: 'var(--lk-success-600)', bg: 'var(--lk-success-50)',
      title: LK_FEATURES.lenderMarketplace ? 'Ready for Lenders' : 'Pre-Underwritten · Ready to Forward',
      desc: LK_FEATURES.lenderMarketplace
        ? 'Your application is cleared and ready to be submitted to the lender marketplace.'
        : 'Your file is pre-underwritten. Forward the packet to your loan officer or realtor any time.',
      cta: LK_FEATURES.lenderMarketplace ? 'Enter Marketplace' : 'View & Forward',
      variant: 'primary',
      onClick: () => onView(app.id),
    },
    in_marketplace: {
      Icon: Landmark, color: 'var(--lk-primary-500)', bg: 'var(--lk-primary-50)',
      title: 'Lenders Are Reviewing',
      desc: 'Your file is live in the lender network. You\'ll be notified as offers arrive.',
      cta: 'View Offers', variant: 'primary', onClick: () => onView(app.id),
    },
    bid_accepted: {
      Icon: CheckCircle, color: 'var(--lk-success-600)', bg: 'var(--lk-success-50)',
      title: 'Offer Accepted',
      desc: 'You\'ve accepted a lender\'s offer. Next steps will be coordinated with your lender.',
      cta: 'View Details', variant: 'secondary', onClick: () => onView(app.id),
    },
    closed: {
      Icon: Home, color: 'var(--lk-success-600)', bg: 'var(--lk-success-50)',
      title: 'Funded!',
      desc: 'Your loan has been funded. Welcome to your new home!',
      cta: 'View Summary', variant: 'secondary', onClick: () => onView(app.id),
    },
    needs_work: {
      Icon: AlertTriangle, color: 'var(--lk-danger-600)', bg: 'var(--lk-danger-50)',
      title: 'Action Required',
      desc: 'Your application needs attention. Review the underwriter\'s notes and update the required items.',
      cta: 'Review & Fix', variant: 'danger', onClick: () => onContinue(app.id),
    },
  };

  const action = actions[status] || actions.draft;
  const ActionIcon = action.Icon;

  return (
    <div style={{
      padding: 'var(--lk-space-5) var(--lk-space-6)', borderRadius: 'var(--lk-radius-lg)',
      backgroundColor: action.bg, border: '1px solid var(--lk-border-subtle)',
      display: 'flex', alignItems: 'center', gap: 'var(--lk-space-4)',
    }}>
      <div style={{
        width: 44, height: 44, borderRadius: 'var(--lk-radius-lg)',
        backgroundColor: 'var(--lk-bg-surface-1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        boxShadow: 'var(--lk-shadow-xs)',
      }}>
        <ActionIcon size={20} color={action.color} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 'var(--lk-text-md)', fontWeight: 600, color: 'var(--lk-text-primary)', marginBottom: 2 }}>{action.title}</div>
        <div style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-secondary)' }}>{action.desc}</div>
      </div>
      <Button variant={action.variant} onClick={action.onClick} style={{ flexShrink: 0 }}>
        {action.cta}
      </Button>
    </div>
  );
};

// ── Owen Summary Card ───────────────────────────────────────────
const OwenSummaryCard = ({ app, borrowerName }) => {
  const score = app?.readiness_score || 0;
  const status = app?.status || 'draft';

  const getMessage = () => {
    if (!app) return `Welcome, ${borrowerName}! Start your assessment to see where you stand.`;
    if (score === 0) return `Let's get started, ${borrowerName}. Complete your assessment to see your Readiness Score.`;
    if (score < 30) return `You're getting started, ${borrowerName}. Keep answering questions — each one helps me find better programs for you.`;
    if (score < 50) return `Good progress, ${borrowerName}. You're building momentum. Uploading income documents would be your highest-impact next step.`;
    if (score < 70) return `Strong profile, ${borrowerName}. You're in a solid position. A few more details and your file will be ready to share.`;
    if (status === 'in_marketplace') return `Your file is live, ${borrowerName}. Lenders in the network are reviewing it now.`;
    if (status === 'bid_accepted' || status === 'closed') return `Congratulations, ${borrowerName}! You've reached the finish line.`;
    return `Excellent readiness, ${borrowerName}. Your file is strong — your loan officer will love seeing this.`;
  };

  return (
    <div style={{
      padding: 'var(--lk-space-4) var(--lk-space-5)', borderRadius: 'var(--lk-radius-lg)',
      backgroundColor: 'var(--lk-bg-surface-2)', border: '1px solid var(--lk-border)',
      display: 'flex', alignItems: 'flex-start', gap: 'var(--lk-space-3)',
    }}>
      <div style={{
        width: 34, height: 34, borderRadius: '50%',
        background: 'linear-gradient(135deg, var(--lk-primary-500), var(--lk-primary-700))',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        color: 'white', fontSize: 'var(--lk-text-sm)', fontWeight: 700,
      }}>
        O
      </div>
      <div>
        <div style={{ fontSize: 'var(--lk-text-xs)', fontWeight: 600, color: 'var(--lk-text-tertiary)', marginBottom: 4 }}>Owen's Summary</div>
        <div style={{ fontSize: 'var(--lk-text-base)', color: 'var(--lk-text-primary)', lineHeight: 'var(--lk-leading-normal)' }}>{getMessage()}</div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
const BorrowerDashboard = () => {
  const navigate = useNavigate();
  const { startApplication } = useEarlyAccess();
  const [borrower, setBorrower] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllApps, setShowAllApps] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Check for demo session first
      const demoSession = localStorage.getItem('lk_demo_session');
      if (demoSession) {
        const demoUser = JSON.parse(demoSession);
        setBorrower({
          id: demoUser.id,
          email: demoUser.email,
          firstName: demoUser.user_metadata?.firstName || 'Demo',
          lastName: demoUser.user_metadata?.lastName || 'Borrower',
        });
        await fetchApplications(demoUser.id);
        return;
      }

      // Otherwise check Supabase session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate('/borrower/login');
        return;
      }

      setBorrower({
        id: session.user.id,
        email: session.user.email,
        firstName: session.user.user_metadata?.firstName || session.user.email.split('@')[0],
        lastName: session.user.user_metadata?.lastName || '',
      });

      await fetchApplications(session.user.id);
    } catch (error) {
      console.error('Auth error:', error);
      navigate('/borrower/login');
    }
  };

  // Sample demo applications so the dashboard isn't empty for demo users
  const DEMO_APPLICATIONS = [
    {
      id: 'demo-app-001',
      user_id: 'demo-borrower-123',
      anonymous_id: 'LK-7842',
      status: 'in_progress',
      product_type: 'conventional',
      loan_amount: 385000,
      property_value: 480000,
      down_payment: 95000,
      credit_score: 738,
      income_monthly: 9200,
      readiness_score: 62,
      readiness_breakdown: { income: 18, assets: 12, credit: 15, structure: 10, docs: 3, intent: 4 },
      property_info: { address: '742 Evergreen Terrace, Springfield' },
      documents_count: 2,
      created_at: new Date(Date.now() - 3 * 86400000).toISOString(),
      updated_at: new Date(Date.now() - 86400000).toISOString(),
    },
    {
      id: 'demo-app-002',
      user_id: 'demo-borrower-123',
      anonymous_id: 'LK-5219',
      status: 'draft',
      product_type: 'fha',
      loan_amount: 275000,
      property_value: 290000,
      down_payment: 15000,
      credit_score: 680,
      income_monthly: 6800,
      readiness_score: 34,
      readiness_breakdown: { income: 10, assets: 5, credit: 10, structure: 5, docs: 0, intent: 4 },
      property_info: { address: '' },
      documents_count: 0,
      created_at: new Date(Date.now() - 10 * 86400000).toISOString(),
      updated_at: new Date(Date.now() - 8 * 86400000).toISOString(),
    },
  ];

  const fetchApplications = async (userId) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('loan_applications')
        .select('*')
        .eq('applicant_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // If demo user and no real data, use sample applications
      if ((!data || data.length === 0) && userId === 'demo-borrower-123') {
        setApplications(DEMO_APPLICATIONS);
      } else {
        setApplications(data || []);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      // Fallback to demo data for demo user even on error
      if (userId === 'demo-borrower-123') {
        setApplications(DEMO_APPLICATIONS);
      } else {
        setApplications([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    localStorage.removeItem('lk_demo_session');
    await supabase.auth.signOut();
    navigate('/');
  };

  const handleNewApplication = () => {
    startApplication('borrower_dashboard');
  };

  const handleContinueApplication = (id) => {
    navigate(`/apply?resume=${id}`);
  };

  const handleViewApplication = (id) => {
    navigate(`/borrower/application/${id}`);
  };

  const formatCurrency = (amount) => `$${(amount || 0).toLocaleString()}`;

  const formatDate = (date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  // Primary application = most recent non-closed, or most recent overall
  const primaryApp = useMemo(() => {
    if (applications.length === 0) return null;
    const active = applications.find(a => a.status !== 'closed');
    return active || applications[0];
  }, [applications]);

  const score = primaryApp?.readiness_score || 0;
  const tier = getTier(score);
  const journeyIndex = primaryApp ? getJourneyIndex(primaryApp.status) : 0;

  // Approximate category breakdown from server-side score
  // (Server stores total score; we distribute proportionally across categories)
  const categoryBreakdown = useMemo(() => {
    if (!primaryApp || !score) return CATEGORIES.map(c => ({ ...c, score: 0 }));

    // If the app has detailed breakdown stored, use it; otherwise estimate
    const breakdown = primaryApp.readiness_breakdown;
    if (breakdown) {
      return CATEGORIES.map(c => ({ ...c, score: breakdown[c.id] || 0 }));
    }

    // Heuristic estimation from available data
    const hasCredit = !!primaryApp.credit_score;
    const hasIncome = !!primaryApp.income_monthly;
    const hasProduct = !!primaryApp.product_type;
    const hasDocs = (primaryApp.documents_count || 0) > 0;
    const isAdvanced = ['submitted_for_underwriting', 'underwriting_in_progress', 'conditionally_cleared', 'cleared_for_marketplace', 'in_marketplace', 'bid_accepted', 'closed'].includes(primaryApp.status);

    return CATEGORIES.map(c => {
      let est = 0;
      switch (c.id) {
        case 'income':
          est = hasIncome ? Math.min(Math.round(score * 0.25), 25) : Math.round(score * 0.1);
          break;
        case 'assets':
          est = isAdvanced ? Math.min(Math.round(score * 0.2), 20) : Math.round(score * 0.08);
          break;
        case 'credit':
          if (hasCredit) {
            const cs = primaryApp.credit_score;
            est = cs >= 760 ? 20 : cs >= 720 ? 17 : cs >= 680 ? 14 : cs >= 640 ? 10 : cs >= 580 ? 6 : 3;
          } else {
            est = Math.round(score * 0.15);
          }
          break;
        case 'structure':
          est = hasProduct ? Math.min(Math.round(score * 0.15), 15) : Math.round(score * 0.05);
          break;
        case 'docs':
          est = hasDocs ? Math.min((primaryApp.documents_count || 0) * 2, 10) : 0;
          break;
        case 'intent':
          est = isAdvanced ? Math.min(Math.round(score * 0.1), 10) : Math.round(score * 0.03);
          break;
      }
      return { ...c, score: Math.min(est, c.maxPoints) };
    });
  }, [primaryApp, score]);

  if (!borrower) return null;

  const layoutProps = {
    role: 'borrower',
    navItems: BORROWER_NAV,
    user: { name: `${borrower.firstName} ${borrower.lastName}`, email: borrower.email, role: 'borrower' },
    onLogout: handleLogout,
  };

  // ── Empty state ─────────────────────────────────────────────
  if (!loading && applications.length === 0) {
    return (
      <AppLayout {...layoutProps}>
        <div style={{ maxWidth: 560, margin: '60px auto', textAlign: 'center' }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%', margin: '0 auto var(--lk-space-6)',
            background: 'var(--lk-primary-50)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Home size={32} color="var(--lk-primary-500)" />
          </div>
          <h3 className="lk-h3" style={{ marginBottom: 'var(--lk-space-2)' }}>Your Readiness Blueprint</h3>
          <p style={{ fontSize: 'var(--lk-text-md)', color: 'var(--lk-text-tertiary)', lineHeight: 'var(--lk-leading-relaxed)', marginBottom: 'var(--lk-space-8)' }}>
            Answer a few questions and we'll show you exactly where you stand — your buying power, best-fit programs, and a clear path to mortgage readiness.
          </p>
          <Button variant="primary" size="lg" icon={Sparkles} onClick={handleNewApplication}>
            Start My Assessment
          </Button>
          <div style={{ marginTop: 'var(--lk-space-4)', fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-quaternary)' }}>
            Takes about 3 minutes · No credit check required
          </div>
          <div style={{ marginTop: 'var(--lk-space-6)' }}>
            <OwenSummaryCard app={null} borrowerName={borrower.firstName} />
          </div>
        </div>
        <FloatingOwenAssistant context="borrower-dashboard" initialMessage="Welcome! I'm here to help you get started with your mortgage application. Ask me anything!" />
        <FloatingKnowledge context="general" />
      </AppLayout>
    );
  }

  // ── Loading state ───────────────────────────────────────────
  if (loading) {
    return (
      <AppLayout {...layoutProps}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 'var(--lk-space-20) 0' }}>
          <div className="lk-spinner lk-spinner-lg" style={{ marginBottom: 'var(--lk-space-4)' }} />
          <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>Loading your blueprint...</div>
        </div>
      </AppLayout>
    );
  }

  // ── Blueprint view (has applications) ───────────────────────
  return (
    <AppLayout {...layoutProps}>
      {/* ── Header ─────────────────────────────────────────── */}
      <PageHeader
        title="Readiness Blueprint"
        description={primaryApp?.property_info?.address || 'Your mortgage readiness at a glance'}
        actions={
          <Button variant="secondary" size="sm" icon={Plus} onClick={handleNewApplication}>
            New Application
          </Button>
        }
      />

      {/* ── Hero: Score + Journey + Owen ────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 'var(--lk-space-6)', marginBottom: 'var(--lk-space-6)' }}>
        {/* Left: Score ring + tier + product */}
        <Surface padding="var(--lk-space-8)" style={{ textAlign: 'center' }}>
          <ScoreRing score={score} />
          <div style={{ marginTop: 'var(--lk-space-3)', fontSize: 'var(--lk-text-md)', fontWeight: 600, color: tier.color }}>
            {tier.label}
          </div>
          <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)', marginTop: 2 }}>
            Mortgage Readiness Score
          </div>

          {primaryApp?.product_type && (
            <div style={{ marginTop: 'var(--lk-space-4)' }}>
              <Badge variant="primary">
                {primaryApp.product_type.replace(/_/g, ' ')}
              </Badge>
            </div>
          )}

          {primaryApp?.loan_amount > 0 && (
            <div style={{ marginTop: 'var(--lk-space-3)', fontSize: 'var(--lk-text-xl)', fontWeight: 700, color: 'var(--lk-text-primary)', letterSpacing: '-0.02em' }}>
              {formatCurrency(primaryApp.loan_amount)}
              <div style={{ fontSize: 'var(--lk-text-xs)', fontWeight: 400, color: 'var(--lk-text-tertiary)' }}>Loan Amount</div>
            </div>
          )}
        </Surface>

        {/* Right: Journey + Owen + Next Action */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-4)' }}>
          <Surface padding="var(--lk-space-5) var(--lk-space-6)">
            <div className="lk-overline" style={{ marginBottom: 'var(--lk-space-4)' }}>Your Journey</div>
            <JourneyTimeline currentIndex={journeyIndex} />
          </Surface>

          <OwenSummaryCard app={primaryApp} borrowerName={borrower.firstName} />

          <NextActionCard
            app={primaryApp}
            onContinue={handleContinueApplication}
            onView={handleViewApplication}
          />
        </div>
      </div>

      {/* ── Category Breakdown ─────────────────────────────── */}
      <Surface padding="var(--lk-space-6)" style={{ marginBottom: 'var(--lk-space-6)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--lk-space-4)' }}>
          <div>
            <div style={{ fontSize: 'var(--lk-text-lg)', fontWeight: 600, color: 'var(--lk-text-primary)' }}>Score Breakdown</div>
            <div style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-tertiary)' }}>How your readiness score is calculated</div>
          </div>
          <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)' }}>
            {score}/100 total
          </div>
        </div>
        <Grid cols={3} gap="var(--lk-space-4)">
          {categoryBreakdown.map((cat) => (
            <CategoryMiniBar key={cat.id} cat={cat} score={cat.score} maxPoints={cat.maxPoints} />
          ))}
        </Grid>
        <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)', marginTop: 'var(--lk-space-4)', textAlign: 'center' }}>
          This is a readiness indicator, not an approval prediction. Actual lending decisions are made by lenders.
        </div>
      </Surface>

      {/* ── Application List (collapsed by default if >1) ──── */}
      {applications.length > 1 && (
        <div style={{ marginBottom: 'var(--lk-space-6)' }}>
          <button
            onClick={() => setShowAllApps(!showAllApps)}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              fontSize: 'var(--lk-text-base)', fontWeight: 600, color: 'var(--lk-text-link)',
              display: 'flex', alignItems: 'center', gap: 6, marginBottom: 'var(--lk-space-3)',
            }}
          >
            {showAllApps ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            All Applications ({applications.length})
          </button>
          {showAllApps && (
            <Surface padding="0" style={{ overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table className="lk-table">
                  <thead>
                    <tr>
                      <th>Application</th>
                      <th>Product</th>
                      <th style={{ textAlign: 'right' }}>Loan Amount</th>
                      <th style={{ textAlign: 'center' }}>Score</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th style={{ textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.map((app) => (
                      <tr key={app.id} style={{ backgroundColor: app.id === primaryApp?.id ? 'var(--lk-gray-50)' : 'transparent' }}>
                        <td>
                          <div style={{ fontWeight: 500, color: 'var(--lk-text-primary)' }}>{app.property_info?.address || 'New Application'}</div>
                          <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)' }}>ID: {app.anonymous_id || app.id.slice(0, 8)}</div>
                        </td>
                        <td>
                          <Badge variant="neutral">{app.product_type?.replace(/_/g, ' ') || 'Not Selected'}</Badge>
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(app.loan_amount)}</td>
                        <td style={{ textAlign: 'center' }}>
                          {app.readiness_score ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                              <Badge variant={app.readiness_score >= 70 ? 'success' : app.readiness_score >= 50 ? 'primary' : app.readiness_score >= 30 ? 'warning' : 'neutral'}>
                                {app.readiness_score}
                              </Badge>
                              <div style={{ width: 50, height: 4, borderRadius: 2, backgroundColor: 'var(--lk-gray-100)' }}>
                                <div style={{
                                  height: '100%', width: `${app.readiness_score}%`, borderRadius: 2,
                                  backgroundColor: app.readiness_score >= 70 ? 'var(--lk-success-500)' : app.readiness_score >= 50 ? 'var(--lk-primary-500)' : 'var(--lk-warning-500)',
                                }} />
                              </div>
                            </div>
                          ) : '—'}
                        </td>
                        <td>
                          <Badge variant={statusColors[app.status] || 'neutral'}>{statusLabels[app.status] || app.status}</Badge>
                        </td>
                        <td style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-tertiary)' }}>{formatDate(app.created_at)}</td>
                        <td style={{ textAlign: 'center' }}>
                          {app.status === 'draft' || app.status === 'in_progress' ? (
                            <Button size="sm" variant="primary" icon={Play} onClick={() => handleContinueApplication(app.id)}>
                              Continue
                            </Button>
                          ) : (
                            <Button size="sm" variant="ghost" icon={Eye} onClick={() => handleViewApplication(app.id)} />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Surface>
          )}
        </div>
      )}

      {/* Floating AI Assistant & Knowledge */}
      <FloatingOwenAssistant 
        context="borrower-dashboard"
        initialMessage={`You have ${applications.length} application${applications.length > 1 ? 's' : ''}. I'm here to help you navigate your mortgage journey!`}
      />
      <FloatingKnowledge context="general" />
    </AppLayout>
  );
};

export default BorrowerDashboard;
